import { BcmFinancialRecord } from '@shared/models/bcm-financial-record';
import { BcmReceipt } from '@shared/models/bcm-receipt';
import { InvoicePosition } from '@shared/models/invoice-position';
import { ReceiptTypes } from '@shared/invoice-types';
import { tryParseDate } from '@shared/functions/try-parse-date';

export function isCancellationReceipt(receipt: BcmReceipt | null | undefined): boolean {
    return receipt?.receiptType === ReceiptTypes.CancellationReceipt;
}

/** Originalbelegnummer für die Zeile „Stornierter Beleg …“ auf dem Bondrucker. */
export function resolveOriginalReceiptNumberForPrint(
    record: Pick<BcmFinancialRecord, 'cancellationNumber'> | null | undefined,
    receipt: BcmReceipt | null | undefined,
): string | null {
    if (!receipt) {
        return null;
    }

    if (isCancellationReceipt(receipt)) {
        return record?.cancellationNumber?.trim()
            || receipt.cancellationReceipt?.receiptNumber?.trim()
            || null;
    }

    return null;
}

export function resolveReceiptPrintPositions(receipt: BcmReceipt | null | undefined): InvoicePosition[] {
    return receipt?.positions ?? [];
}

/** Storno-Bons drucken das Datum des stornierten Originalbelegs (TSE-Referenz). */
export function resolveReceiptPrintDate(
    record: Pick<BcmFinancialRecord, 'date'> | null | undefined,
    receipt: BcmReceipt | null | undefined,
): Date {
    if (isCancellationReceipt(receipt)) {
        const referenceBookDate = receipt.tseDocument?.documentReference?.documentBookDate;
        if (referenceBookDate) {
            return tryParseDate(referenceBookDate) ?? new Date(referenceBookDate);
        }

        if (record?.date) {
            return record.date;
        }
    }

    return record?.date ?? new Date();
}

/** Positionsliste für den Sofortdruck direkt nach Storno (API liefert oft noch keine Positionen). */
export function buildCancellationReceiptPrintPositions(
    receiptToCancel: BcmReceipt | null | undefined,
): InvoicePosition[] {
    return (receiptToCancel?.positions ?? []).map((position) => new InvoicePosition({
        ...position,
        quantity: -Math.abs(position.quantity ?? 1),
        price: Math.abs(position.price ?? 0),
    } as any));
}

export function enrichReceiptForCancellationPrint(
    receipt: BcmReceipt,
    receiptToCancel: BcmReceipt | null | undefined,
): BcmReceipt {
    const positions = resolveReceiptPrintPositions(receipt);
    const printPositions = positions.length > 0
        ? positions
        : buildCancellationReceiptPrintPositions(receiptToCancel);

    return {
        ...receipt,
        receiptType: ReceiptTypes.CancellationReceipt,
        positions: printPositions,
    };
}
